import { inject, Injector, runInInjectionContext } from '@angular/core';
import { CanActivateFn, ResolveFn, Route, Router } from '@angular/router';
import type { FeatureFlags } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { EntityManifestService, type EntityManifest } from '@workbuddy/services/entities';
import { getEntityBySlug } from '@workbuddy/services/entities/entity.manifest.registry';
import type { EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { isEntityRecordFeatureEnabled } from './entity.operation.route.guard';
import { ENTITY_NO_ACCESS_PATH, ENTITY_RECORD_PATH } from './entity.record.contracts';

/**
 * Whether this tenant and user may open an entity's generic list: the entity has a menu entry, its
 * rollout flag is on, and its rights pass. The menu entry is required because it is what declares the
 * list as a navigation destination — an entity without one has no list page, only embedded boards.
 */
export function isEntityListAvailable(manifest: EntityManifest | undefined, flags: Partial<Record<FeatureFlags, boolean>> | undefined, runAuthorize: (authorize: () => boolean) => boolean): boolean {

  if (!manifest?.menu) return false;
  if (!isEntityRecordFeatureEnabled(manifest.featureFlag, flags)) return false;

  const authorize = manifest.menu.authorize;
  if (!authorize) return true;

  try {
    return runAuthorize(authorize);
  }
  catch (error) {
    // "Could not determine whether you may" is not "yes".
    console.error(`[EntityList] authorize failed for ${manifest.entity}`, error);
    return false;
  }
}

function resolveSlugEntity(slug: string | null) {

  return slug ? getEntityBySlug(slug) : undefined;
}

/** Refuses before the board mounts: the URL is untrusted input. Denials land on the no-access page. */
export const entityListRouteGuard: CanActivateFn = route => {

  const manifests = inject(EntityManifestService);
  const injector = inject(Injector);
  const router = inject(Router);
  const entity = resolveSlugEntity(route.paramMap.get('slug'));
  const manifest = entity ? manifests.getManifest(entity) : undefined;

  return isEntityListAvailable(manifest, ConfigService.environment.featureFlags, authorize => runInInjectionContext(injector, authorize))
    ? true
    : router.parseUrl(`/${ENTITY_NO_ACCESS_PATH}`);
};

/** The board reads its entity from the `context` input; a resolver supplies what a static route puts in `data`. */
export const entityListContextResolver: ResolveFn<EntityBoardContext> = route => {

  return { entity: resolveSlugEntity(route.paramMap.get('slug'))! };
};

const entityListTitleResolver: ResolveFn<string> = route => {

  const entity = resolveSlugEntity(route.paramMap.get('slug'));
  return (entity ? inject(EntityManifestService).getManifest(entity)?.menu?.label : undefined) ?? '';
};

/**
 * One list route for every entity that declares a `menu`, at `/entity/:slug`.
 *
 * **Declare it after `entityNoAccessRoute`.** `/entity/no-access` also matches `entity/:slug`; the
 * router takes the first match, and no-access must win.
 */
export const entityListRoute: Route = {
  path: `${ENTITY_RECORD_PATH}/:slug`,
  loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board/entity.board.component').then(m => m.EntityBoardComponent),
  canActivate: [entityListRouteGuard],
  resolve: { context: entityListContextResolver },
  title: entityListTitleResolver,
  // A slug change swaps the board's entity, so the guard and resolver must re-run on it.
  runGuardsAndResolvers: 'pathParamsChange',
  // The side nav reads `data.title` / `data.restrictions` on every shell child; this one is not itself a nav item.
  // `topLevel` gives the board its full-page chrome (entityBoardTopLevel), as the old `/finance-pro` group did for its boards.
  data: { hidden: true, restrictions: [], topLevel: true },
};
